/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Guardian Dashboard Component
 * Shows family management and children progress for guardian accounts
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useOrganizationStore } from '../stores/organizationStore'
import api from '../services/api'
import coachService from '../services/coachService'
import { Users, TrendingUp, BookOpen, UserPlus, UserCheck, MessageSquare } from 'lucide-react'
import LoadingSpinner from './LoadingSpinner'
import TutoringAgreementModal from './tutoring/TutoringAgreementModal'
import SeatLimitBadge from './SeatLimitBadge'
import InviteChildModal from './InviteChildModal'
import PendingInvitationsPanel from './PendingInvitationsPanel'
import ChildProgressCard from './ChildProgressCard'
import CoachingActionsBanner from './CoachingActionsBanner'
import CoachApprovalCard from './CoachApprovalCard'
import messagingService from '../services/messagingService'
import toast from 'react-hot-toast'
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus'
import { createLogger } from '../utils/logger'

const logger = createLogger('GuardianDashboard')

const GuardianDashboard = () => {
  const { user } = useAuthStore()
  const { activeOrganization } = useOrganizationStore()
  const navigate = useNavigate()
  const location = useLocation()

  const [children, setChildren] = useState([])
  const [childrenLoading, setChildrenLoading] = useState(true)
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [invitationsLoading, setInvitationsLoading] = useState(true)
  const [coachApprovals, setCoachApprovals] = useState([])
  const [coachApprovalsLoading, setCoachApprovalsLoading] = useState(true)
  const [seatInfo, setSeatInfo] = useState(null)
  const [seatInfoLoading, setSeatInfoLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  // relationshipId of the child's coach whose guardian consent the guardian is signing
  const [agreementRelId, setAgreementRelId] = useState(null)
  const [familyStats, setFamilyStats] = useState({
    totalChildren: 0,
    totalChallenges: 0,
    averageProgress: 0
  })
  const [unreadMessages, setUnreadMessages] = useState(0)
  // Tracks whether the one-shot freshLogin marker has already been cleared (see effect below).
  const freshLoginCleared = useRef(false)

  // Refresh every dashboard dataset. Defined once and used by both the mount/org-change effect and the
  // tab-refocus handler, so a future loader can't be added to one path but forgotten in the other.
  // (Closes over the load* functions defined below; only ever invoked after mount, when they exist.)
  const refreshAll = () => {
    loadFamilyData()
    loadSeatInfo()
    loadPendingInvitations()
    loadCoachApprovals()
    loadUnreadMessages()
  }
  // Invoke refreshAll through a ref so the org-change effect doesn't list it as a dependency: it is a
  // new identity every render, so depending on it (e.g. via an exhaustive-deps auto-fix) would turn
  // this mount/org-change effect into a per-render effect that floods the server with reloads.
  const refreshAllRef = useRef(refreshAll)
  refreshAllRef.current = refreshAll

  // Load on mount and whenever the active organization changes. `freshLogin` is intentionally NOT
  // a dependency: it was clearing only after this effect ran, so navigating away and back (which
  // replaces location state) re-triggered a redundant full reload. useRefetchOnFocus below already
  // keeps the data fresh on return.
  useEffect(() => {
    refreshAllRef.current()
  }, [activeOrganization])

  // One-shot: clear the freshLogin marker that login navigation leaves in location state so it
  // doesn't linger across subsequent navigations. Runs once on mount.
  useEffect(() => {
    if (!freshLoginCleared.current && location.state?.freshLogin) {
      freshLoginCleared.current = true
      window.history.replaceState({}, document.title)
    }
  }, [location.state?.freshLogin])

  // Mirror the on-mount load when the guardian returns to the tab, so anything that changed elsewhere
  // (an approval/sign, an accepted invitation, a new message, a seat change) stops showing stale
  // without a manual reload.
  useRefetchOnFocus(refreshAll)

  const loadFamilyData = async () => {
    try {
      setChildrenLoading(true)

      // Fetch children with statistics and coaches
      const response = await api.get('/family/children', {
        params: { includeStats: true, includeCoaches: true }
      })

      const childrenData = response.data.children || []
      setChildren(childrenData)

      // Calculate family-level statistics
      const totalChallenges = childrenData.reduce(
        (sum, child) => sum + (child.stats?.totalChallenges || 0),
        0
      )

      const totalProgress = childrenData.reduce(
        (sum, child) => sum + (child.stats?.averageScore || 0),
        0
      )

      setFamilyStats({
        totalChildren: childrenData.length,
        totalChallenges,
        averageProgress:
          childrenData.length > 0
            ? Math.round((totalProgress / childrenData.length) * 100) / 100
            : 0
      })
    } catch (error) {
      logger.error('Error loading family data:', error)
      toast.error('Failed to load family data')
    } finally {
      setChildrenLoading(false)
    }
  }

  const loadSeatInfo = async () => {
    try {
      setSeatInfoLoading(true)
      const response = await api.get('/family/seats')

      if (response.data.success) {
        setSeatInfo(response.data.seats)
      }
    } catch (error) {
      logger.error('Error loading seat info:', error)
    } finally {
      setSeatInfoLoading(false)
    }
  }

  const loadPendingInvitations = async () => {
    try {
      setInvitationsLoading(true)

      // Verify activeOrganization exists and belongs to current user
      // This prevents stale cached organization data from causing 403 errors
      if (
        !activeOrganization?.id ||
        !user?.organizations?.some((org) => org.id === activeOrganization.id)
      ) {
        setPendingInvitations([])
        return
      }

      // Get all invitations for the organization
      const response = await api.get(`/invitations/organization/${activeOrganization.id}`)

      if (response.data.success) {
        const pending = response.data.invitations.filter((inv) => inv.status === 'pending')
        setPendingInvitations(pending)
      }
    } catch (error) {
      // If endpoint doesn't exist yet, silently fail
      if (error.response?.status !== 404) {
        logger.error('Error loading pending invitations:', error)
      }
      setPendingInvitations([])
    } finally {
      setInvitationsLoading(false)
    }
  }

  const loadCoachApprovals = async () => {
    try {
      setCoachApprovalsLoading(true)
      const response = await coachService.getGuardianCoachApprovals()

      if (response.success) {
        setCoachApprovals(response.pendingRequests || [])
      }
    } catch (error) {
      // If endpoint doesn't exist yet, silently fail
      if (error.response?.status !== 404) {
        logger.error('Error loading coach approvals:', error)
      }
      setCoachApprovals([])
    } finally {
      setCoachApprovalsLoading(false)
    }
  }

  const loadUnreadMessages = async () => {
    try {
      const response = await messagingService.getUnreadCount()
      if (response.success) {
        setUnreadMessages(response.unreadCount || 0)
      }
    } catch (error) {
      logger.error('Error loading unread messages:', error)
    }
  }

  const handleCoachApproved = (relationshipId) => {
    setCoachApprovals((prev) => prev.filter((r) => r.id !== relationshipId))
  }

  const handleCoachDeclined = (relationshipId) => {
    setCoachApprovals((prev) => prev.filter((r) => r.id !== relationshipId))
  }

  const handleInviteCreated = (invitation) => {
    setPendingInvitations((prev) => [invitation, ...prev])
    loadSeatInfo() // Refresh seat info
  }

  const handleInvitationCancelled = (invitationId) => {
    setPendingInvitations((prev) => prev.filter((inv) => inv.id !== invitationId))
    loadSeatInfo() // Refresh seat info
  }

  const formatTime = (seconds) => {
    if (!seconds) return '0m'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const seatsAvailable = seatInfo?.children?.available || 0
  const childSeatsUsed = seatInfo?.children?.used || 0
  const childSeatsMax = seatInfo?.children?.max || 2

  return (
    <div className="space-y-6">
      {/* ===== Notifications (kept at the very top of the screen) ===== */}

      {/* Pending Invitations */}
      {!invitationsLoading && pendingInvitations.length > 0 && (
        <PendingInvitationsPanel
          invitations={pendingInvitations}
          onInvitationCancelled={handleInvitationCancelled}
        />
      )}

      {/* Tutoring consents awaiting the guardian's signature */}
      <CoachingActionsBanner />

      {/* Pending Coach Approvals */}
      {!coachApprovalsLoading && coachApprovals.length > 0 && (
        <div className="card bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
          <div className="card-body">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200">
                  Coach Requests ({coachApprovals.length})
                </h3>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  Coaches waiting for your approval to work with your children
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {coachApprovals.map((request) => (
                <CoachApprovalCard
                  key={request.id}
                  request={request}
                  onApproved={handleCoachApproved}
                  onDeclined={handleCoachDeclined}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Welcome Section with Seat Badge */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                Welcome back{user?.firstName ? `, ${user.firstName}` : ''}!
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {activeOrganization
                  ? `Managing ${activeOrganization.name}`
                  : 'Manage your family and track progress'}
              </p>
            </div>

            {/* Seat Limit Badge and Action Buttons */}
            <div className="flex items-center gap-3">
              {!seatInfoLoading && seatInfo && (
                <SeatLimitBadge
                  used={childSeatsUsed}
                  max={childSeatsMax}
                  type="children"
                  members={seatInfo.children?.members}
                  pendingInvitations={seatInfo.children?.pendingInvitations}
                />
              )}
              <button
                onClick={() => navigate('/messages')}
                className="btn btn-secondary inline-flex items-center gap-2 relative"
                title="Messages"
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Messages</span>
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowInviteModal(true)}
                disabled={seatsAvailable <= 0}
                className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title={seatsAvailable <= 0 ? 'No available seats' : 'Add child to family'}
              >
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Child</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Family Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Children
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {childrenLoading ? '...' : familyStats.totalChildren}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-success-100 dark:bg-success-900/30 rounded-lg flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-success-600 dark:text-success-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Challenges
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {childrenLoading ? '...' : familyStats.totalChallenges}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-warning-100 dark:bg-warning-900/30 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-warning-600 dark:text-warning-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Average Progress
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {childrenLoading
                    ? '...'
                    : familyStats.totalChallenges === 0
                      ? 'No data'
                      : `${familyStats.averageProgress}%`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Children Overview */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Children & Progress
          </h3>
        </div>

        {childrenLoading ? (
          <div className="card">
            <div className="card-body flex items-center justify-center py-8">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading children...</span>
            </div>
          </div>
        ) : children.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-12">
              <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No children added yet
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Add your first child to start tracking their progress
              </p>
              <button
                onClick={() => setShowInviteModal(true)}
                disabled={seatsAvailable <= 0}
                className="btn btn-primary inline-flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Add Child
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {children.map((child) => (
              <ChildProgressCard
                key={child.id}
                child={child}
                formatTime={formatTime}
                onSignConsent={setAgreementRelId}
              />
            ))}
          </div>
        )}
      </div>
      {/* Invite Child Modal */}
      <InviteChildModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInviteCreated={handleInviteCreated}
        seatsAvailable={seatsAvailable}
      />

      {/* Tutoring Agreement Modal (guardian signs consent for a child's coach) */}
      <TutoringAgreementModal
        isOpen={!!agreementRelId}
        relationshipId={agreementRelId}
        signerRole="guardian"
        onClose={() => setAgreementRelId(null)}
        onSigned={() => loadFamilyData()}
      />
    </div>
  )
}

export default GuardianDashboard
